import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, setTokens } from '../../api/client'

export function CallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      const error = searchParams.get('error')
      const message = searchParams.get('message')

      // Handle OAuth errors
      if (error) {
        setStatus('error')
        setErrorMessage(message || error)
        setTimeout(() => {
          navigate(`/login?error=${error}&message=${message || ''}`, { replace: true })
        }, 2000)
        return
      }

      // Check for session token from OAuth redirect
      const token = searchParams.get('token')

      if (token) {
        // Store session token - use it as both access and refresh for now
        // The backend uses session tokens (dses_*) not JWT access/refresh tokens
        setTokens(token, token)

        // Verify authentication by calling /me endpoint
        try {
          await api.me.get()
          // Successfully authenticated - redirect to dashboard
          navigate('/', { replace: true })
        } catch (err) {
          // Not authenticated - redirect to login
          console.error('Auth verification failed:', err)
          setStatus('error')
          setErrorMessage('Authentication failed. Please try again.')
          setTimeout(() => {
            navigate('/login', { replace: true })
          }, 2000)
        }
        return
      }

      // Legacy flow: check for access_token/refresh_token params
      const accessToken = searchParams.get('access_token')
      const refreshToken = searchParams.get('refresh_token')

      if (accessToken && refreshToken) {
        setTokens(accessToken, refreshToken)
        navigate('/', { replace: true })
        return
      }

      // No valid params - redirect to login
      navigate('/login', { replace: true })
    }

    handleCallback()
  }, [searchParams, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)]">
      <div className="text-center">
        {status === 'loading' ? (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent)] mx-auto mb-4" />
            <p className="text-[var(--text-secondary)]">Completing sign in...</p>
          </>
        ) : (
          <>
            <div className="text-red-500 mb-4">
              <svg className="h-8 w-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-[var(--text-primary)] font-medium mb-2">Sign in failed</p>
            <p className="text-[var(--text-secondary)] text-sm">{errorMessage}</p>
            <p className="text-[var(--text-muted)] text-xs mt-2">Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  )
}
